/**
 * TendrilChatPanel — overlay drawer chat panel.
 *
 * Width: 380px desktop (≥1024px), 320px tablet (640–1023px), full-width
 * bottom sheet (≤639px) — enforced via CSS media queries in tendril.css.
 *
 * ARIA:
 *   - role="complementary" normally
 *   - role="dialog" + focus trap when PendingActionCard is active
 *   - aria-live="polite" on message list so screen readers announce replies
 *
 * Focus management:
 *   - When panel opens, focus moves to the text input
 *   - Escape key closes the panel
 *   - Close button in header
 *
 * BEM classes: .tendril-panel, .tendril-panel__*, .tendril-starters,
 * .tendril-starters__chip
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PendingActionCard from './PendingActionCard.jsx';
import ShowMeChip from './ShowMeChip.jsx';
import FeedbackControls from './FeedbackControls.jsx';
import TendrilStatusChecklist from './TendrilStatusChecklist.jsx';
import TendrilStatusAnnouncer from './TendrilStatusAnnouncer.jsx';
import { useTendrilChat } from '../../hooks/useTendrilChat.js';

// R3 (user-feedback redesign): editable pre-fill starters shown only in the
// empty state (no messages yet). Clicking one pre-fills the input — it never
// sends automatically, so the user can edit before submitting.
const STARTERS = [
  "I'd like to leave feedback…",
  'I have a feature request…',
  'I found a bug…',
  'How do I…',
];

// Matches FEEDBACK_EXCERPT_MAX_LENGTH in backend/src/handlers/tendril.ts —
// truncate client-side so the excerpt threaded into FeedbackControls (and
// ultimately the /api/tendril/feedback POST body) is never longer than what
// the backend would persist anyway.
const FEEDBACK_EXCERPT_MAX_LENGTH = 2000;
import { markdownToHtml } from '../../lib/tendril/markdownToHtml.js';
import '../../styles/components/tendril.css';

/**
 * Terminal badge/footer/CTA shape for a streamed turn that ended
 * degraded/aborted/errored (tendril-streaming-responses Phase 3, WI-008).
 * Copy is pinned by the creative doc — do not invent alternate wording.
 * Returns null for a fully-ok turn (no badge needed) and for legacy
 * buffered-path `isError` messages (their existing plain-text rendering is
 * untouched — this only applies to streaming-originated messages, which
 * always carry an `errorKind` field, even if null).
 */
function getTerminalStatus(msg) {
  if (msg.errorKind === undefined) return null; // not a streaming-originated message

  if (msg.outcome === 'degraded') {
    return { badgeText: 'PARTIAL ANSWER', badgeClass: 'tendril-badge--degraded-soft', footerText: null, ctas: [] };
  }
  if (msg.outcome === 'aborted') {
    return {
      badgeText: 'CUT OFF',
      badgeClass: 'tendril-badge--degraded-hard',
      footerText: 'Tendril ran out of time before finishing this answer.',
      ctas: [
        { key: 'narrow', label: 'Try a narrower question', variant: 'primary' },
        { key: 'retry', label: 'Retry anyway', variant: 'secondary' },
      ],
    };
  }
  if (msg.errorKind === 'stall') {
    return {
      badgeText: null,
      badgeClass: null,
      footerText: "Connection interrupted. Tendril's response stopped partway through.",
      ctas: [{ key: 'retry', label: 'Retry', variant: 'primary' }],
    };
  }
  if (msg.errorKind === 'stream_error') {
    return {
      badgeText: null,
      badgeClass: null,
      footerText: 'Something interrupted this response.',
      ctas: [{ key: 'retry', label: 'Retry', variant: 'primary' }],
    };
  }
  return null;
}

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 * }} props
 */
export default function TendrilChatPanel({ isOpen, onClose }) {
  const {
    messages,
    loading,
    pendingAction,
    conversationId,
    sendMessage,
    sendConfirm,
    cancelPendingAction,
    clearChat,
  } = useTendrilChat();

  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);
  const messageRefs = useRef({});
  const pendingFocusRef = useRef(false);
  const lastAnnouncedKeyRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [announcement, setAnnouncement] = useState('');
  // R3: single pageContext capture point, sourced from the router (not
  // window.location) — TendrilChatPanel is always rendered under the app's
  // <Outlet> (see AppLayout.jsx), so useLocation() is safe here.
  const location = useLocation();

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Escape key handler + focus trap when pendingAction is active (role="dialog")
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap: when acting as role="dialog" (pendingAction active), keep
      // Tab/Shift+Tab within the panel so keyboard users cannot exit the dialog.
      if (e.key === 'Tab' && pendingAction && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) return;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, pendingAction]);

  // JD-8 fix: the composer is no longer cleared eagerly before the await —
  // the outcome the hook resolves decides whether to clear (ok/degraded, a
  // genuinely-answered turn) or restore the original text with the caret at
  // the end (aborted/error, the same pattern handleStarterClick uses below).
  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || loading) return;

    const result = await sendMessage(text, { url: location.pathname });

    if (result && (result.outcome === 'ok' || result.outcome === 'degraded')) {
      setInputValue('');
      if (inputRef.current) inputRef.current.value = '';
    } else {
      setInputValue(text);
      const el = inputRef.current;
      if (el) {
        el.value = text;
        el.focus();
        el.setSelectionRange(text.length, text.length);
      }
    }
  }, [inputValue, loading, sendMessage, location.pathname]);

  // WI-008 accessibility: after a retry-CTA-triggered resend completes,
  // focus moves to the newly appended assistant message wrapper once.
  useEffect(() => {
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    const last = messages[messages.length - 1];
    if (!last) return;
    const el = messageRefs.current[last.id];
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  }, [messages]);

  const handleRetry = useCallback(
    async (originalText) => {
      pendingFocusRef.current = true;
      await sendMessage(originalText, { url: location.pathname });
    },
    [sendMessage, location.pathname],
  );

  // WI-008 accessibility (JD-10): announce ONLY discrete turn transitions —
  // never per status line, never per token. Keyed by message id + phase so
  // the same transition on the same message never re-announces.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.isError) return;

    let key = null;
    let text = null;
    if (last.isStreaming && !last.content) {
      key = `${last.id}:start`;
      text = 'Tendril is responding.';
    } else if (last.isStreaming && last.content) {
      key = `${last.id}:first-token`;
      text = 'Tendril started answering.';
    } else if (!last.isStreaming && last.outcome === 'ok') {
      key = `${last.id}:done-ok`;
      text = 'Tendril finished responding.';
    } else if (!last.isStreaming && last.outcome === 'degraded') {
      key = `${last.id}:degraded-soft`;
      text = 'Tendril finished with a partial answer.';
    } else if (!last.isStreaming && last.outcome === 'aborted') {
      key = `${last.id}:degraded-hard`;
      text = "Tendril's response was cut off.";
    } else if (!last.isStreaming && last.errorKind) {
      key = `${last.id}:error`;
      text = 'Tendril hit an error responding.';
    }

    if (key && key !== lastAnnouncedKeyRef.current) {
      lastAnnouncedKeyRef.current = key;
      setAnnouncement(text);
    }
  }, [messages]);

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // R3: clicking a starter pre-fills the input (never sends), focuses it, and
  // places the caret at the end so the user can immediately keep typing.
  // We write `el.value` directly (in addition to the React state update) so
  // the caret position set via setSelectionRange is correct synchronously —
  // it doesn't need to wait for React to commit the controlled value.
  const handleStarterClick = useCallback((text) => {
    setInputValue(text);
    const el = inputRef.current;
    if (el) {
      el.value = text;
      el.focus();
      el.setSelectionRange(text.length, text.length);
    }
  }, []);

  if (!isOpen) return null;

  // role="dialog" when pendingAction is active (focus trap semantics)
  const role = pendingAction ? 'dialog' : 'complementary';
  const ariaLabel = pendingAction
    ? 'Tendril chat — action confirmation required'
    : 'Tendril AI assistant';

  return (
    <aside
      ref={panelRef}
      className="tendril-panel"
      role={role}
      aria-label={ariaLabel}
      aria-modal={pendingAction ? 'true' : undefined}
    >
      {/* Header */}
      <div className="tendril-panel__header">
        <h2 className="tendril-panel__title">Tendril Assistant</h2>
        <div className="tendril-panel__header-actions">
          {messages.length > 0 && (
            <button
              className="tendril-panel__new-chat-btn"
              onClick={clearChat}
              aria-label="Start a new conversation"
              title="New chat"
              type="button"
            >
              New chat
            </button>
          )}
          <button
            className="tendril-panel__close-btn"
            onClick={onClose}
            aria-label="Close Tendril assistant"
            type="button"
          >
            ×
          </button>
        </div>
      </div>

      <TendrilStatusAnnouncer announcement={announcement} />

      {/* Messages */}
      <div
        className="tendril-panel__messages"
        aria-live="polite"
        aria-atomic="false"
        aria-label="Chat messages"
      >
        {messages.length === 0 && (
          <div className="tendril-panel__message tendril-panel__message--assistant">
            Hi! I can help you browse workshops, manage your AI initiatives, and more.
          </div>
        )}

        {messages.length === 0 && (
          <div className="tendril-starters">
            {STARTERS.map((starter) => (
              <button
                key={starter}
                type="button"
                className="tendril-starters__chip"
                onClick={() => handleStarterClick(starter)}
              >
                {starter}
              </button>
            ))}
          </div>
        )}

        {(() => {
          // 0-based ordinal of each assistant message among all assistant
          // messages in the conversation — computed once per render so the
          // .map() below can look it up by message index without mutating
          // shared state.
          let assistantOrdinal = -1;
          const assistantTurnIndexByIdx = messages.map((m) => {
            if (m.role === 'assistant') {
              assistantOrdinal += 1;
              return assistantOrdinal;
            }
            return null;
          });

          return messages.map((msg, idx) => {
          // Use the structured isError flag set by useTendrilChat (not string matching)
          const isError = msg.role === 'assistant' && msg.isError === true;
          // WI-008 inversion: qualifying = any TERMINAL assistant reply,
          // including error/degraded/aborted outcomes — only a message still
          // mid-stream (isStreaming true) is excluded. This is a deliberate
          // behavior change from the old "!isError" rule: a thumbs-down on a
          // failed/partial turn is exactly the signal feedback analysis needs.
          const isQualifyingAssistantMessage = msg.role === 'assistant' && !msg.isStreaming;
          const terminalStatus = msg.role === 'assistant' ? getTerminalStatus(msg) : null;
          const feedbackOutcome = msg.outcome ?? (isError ? 'error' : 'ok');

          const messageClass = [
            'tendril-panel__message',
            msg.role === 'user'
              ? 'tendril-panel__message--user'
              : isError
              ? 'tendril-panel__message--error'
              : 'tendril-panel__message--assistant',
          ].join(' ');

          // Render assistant messages as formatted markdown HTML. Only the
          // legacy synthetic `isError` fallback string stays plain text —
          // degraded/streamed-error bodies are real model output and MUST
          // render through the markdown path (WI-008, binding).
          const messageContent =
            msg.role === 'assistant' && !isError ? (
              <div
                ref={(el) => {
                  messageRefs.current[msg.id ?? idx] = el;
                }}
                tabIndex={-1}
                className={messageClass}
                aria-live={msg.isStreaming ? 'off' : undefined}
                aria-busy={msg.isStreaming ? 'true' : undefined}
                // markdownToHtml escapes all raw HTML before applying inline tags,
                // so this is safe against XSS even for untrusted model output.
                dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.content) }}
              />
            ) : (
              <div
                ref={(el) => {
                  messageRefs.current[msg.id ?? idx] = el;
                }}
                tabIndex={-1}
                className={messageClass}
              >
                {msg.content}
              </div>
            );

          // Whole-turn duration, not just the tool phase. This previously
          // subtracted the first status line's timestamp from the last one,
          // which measured only the tool-calling window — the first live UAT
          // displayed "· 5s" for a turn the Lambda reported as 12.9s, because
          // token generation after the last tool call was not counted.
          // turnStartedAt/turnEndedAt bracket the entire turn (see
          // useTendrilChat); an in-flight turn measures against now.
          const turnStart = msg.turnStartedAt ?? msg.statusLines?.[0]?.startedAt ?? null;
          const turnEnd = msg.turnEndedAt ?? (msg.isStreaming ? Date.now() : null);
          const elapsedSeconds =
            turnStart && turnEnd ? Math.max(0, Math.round((turnEnd - turnStart) / 1000)) : 0;

          return (
            <React.Fragment key={idx}>
              {/* The thinking trace renders ABOVE the answer: it is the work that
                  produced the reply, so top-to-bottom follows the real sequence.
                  It used to render below, where it read as a footnote. */}
              {msg.role === 'assistant' && msg.statusLines && msg.statusLines.length > 0 && (
                <TendrilStatusChecklist
                  statusLines={msg.statusLines}
                  hasFirstToken={Boolean(msg.content)}
                  elapsedSeconds={elapsedSeconds}
                />
              )}

              {messageContent}

              {/* Terminal degraded/aborted/error badge + footer + CTAs (WI-008) */}
              {terminalStatus && (
                <div
                  className={['tendril-terminal-status', terminalStatus.badgeClass].filter(Boolean).join(' ')}
                >
                  {terminalStatus.badgeText && (
                    <span className="tendril-badge">{terminalStatus.badgeText}</span>
                  )}
                  {terminalStatus.footerText && (
                    <p className="tendril-terminal-status__footer">{terminalStatus.footerText}</p>
                  )}
                  {terminalStatus.ctas.map((cta) => (
                    <button
                      key={cta.key}
                      type="button"
                      className={`tendril-terminal-status__cta tendril-terminal-status__cta--${cta.variant}`}
                      onClick={() =>
                        cta.key === 'narrow'
                          ? handleStarterClick(msg.originalUserText ?? '')
                          : handleRetry(msg.originalUserText ?? '')
                      }
                    >
                      {cta.label}
                    </button>
                  ))}
                </div>
              )}

              {/* PendingActionCard for assistant messages with pendingAction */}
              {msg.role === 'assistant' &&
                msg.pendingAction &&
                pendingAction === msg.pendingAction && (
                  <PendingActionCard
                    pendingAction={msg.pendingAction}
                    onConfirm={sendConfirm}
                    onCancel={cancelPendingAction}
                    loading={loading}
                  />
                )}

              {/* ShowMeChip for assistant messages with ui_actions */}
              {msg.role === 'assistant' &&
                msg.uiActions &&
                msg.uiActions
                  .filter((a) => a.kind === 'navigate')
                  .map((action, actionIdx) => (
                    <ShowMeChip
                      key={`${idx}-action-${actionIdx}`}
                      action={action}
                    />
                  ))}

              {/* FeedbackControls for qualifying assistant replies (tendril-feedback-capture Phase 2 / WI-008) */}
              {isQualifyingAssistantMessage && (
                <FeedbackControls
                  conversationId={conversationId}
                  turnIndex={assistantTurnIndexByIdx[idx]}
                  outcome={feedbackOutcome}
                  assistantMessageExcerpt={
                    typeof msg.content === 'string'
                      ? msg.content.slice(0, FEEDBACK_EXCERPT_MAX_LENGTH)
                      : msg.content
                  }
                />
              )}
            </React.Fragment>
          );
          });
        })()}

        {/* Also render PendingActionCard for the active pendingAction if it
            comes from the current state (not from a message) */}
        {pendingAction &&
          !messages.some((m) => m.pendingAction === pendingAction) && (
            <PendingActionCard
              pendingAction={pendingAction}
              onConfirm={sendConfirm}
              onCancel={cancelPendingAction}
              loading={loading}
            />
          )}

        {loading && (
          <div className="tendril-panel__loading" aria-label="Tendril is thinking">
            <span className="tendril-panel__loading-dot" />
            <span className="tendril-panel__loading-dot" />
            <span className="tendril-panel__loading-dot" />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input row */}
      <div className="tendril-panel__input-row">
        <textarea
          ref={inputRef}
          className="tendril-panel__input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Chat with Banyan AI Portal…"
          disabled={loading}
          rows={1}
          aria-label="Chat message input"
        />
        <button
          className="tendril-panel__send-btn"
          onClick={handleSend}
          disabled={loading || !inputValue.trim()}
          type="button"
          aria-label="Send message"
        >
          Send
        </button>
      </div>
    </aside>
  );
}
